/**
 * FlashSync Cryptography Module for Web UI
 * Uses the Web Crypto API when available (HTTPS/localhost secure contexts)
 * and falls back to a pure-JS implementation (node-forge) when crypto.subtle
 * is unavailable (plain HTTP deployments on internal networks).
 *
 * All fallback output is byte-compatible with the native Web Crypto
 * implementations (PBKDF2-SHA256, AES-256-GCM with appended 128-bit tag).
 */

import forge from 'node-forge/dist/forge.min.js';

class FlashSyncCrypto {
  constructor() {
    this.subtle = window.crypto && window.crypto.subtle ? window.crypto.subtle : null;
    this.encoder = new TextEncoder();
    this.decoder = new TextDecoder();
  }

  /**
   * Derives an encryption key from the master password
   * @param {string} masterPassword - The user's master password
   * @param {Uint8Array|ArrayBuffer} salt - Salt for key derivation (should be stored)
   * @returns {Promise<Object>} - The derived key (CryptoKey natively, Uint8Array in fallback) and salt
   */
  async deriveKeyFromPassword(masterPassword, salt) {
    // If no salt is provided, generate a new one
    if (!salt) {
      salt = window.crypto.getRandomValues(new Uint8Array(16));
    }

    if (this.subtle) {
      // Convert password to key material
      const passwordBuffer = this.encoder.encode(masterPassword);

      // Import the password as raw key material
      const keyMaterial = await this.subtle.importKey(
        'raw',
        passwordBuffer,
        { name: 'PBKDF2' },
        false,
        ['deriveKey']
      );

      // Derive the actual encryption key using PBKDF2
      const key = await this.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: salt,
          iterations: 100000, // High iteration count for security
          hash: 'SHA-256'
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        true, // Extractable for session persistence
        ['encrypt', 'decrypt']
      );

      return { key, salt };
    }

    // Fallback: pure-JS PBKDF2
    const saltBytes = salt instanceof Uint8Array ? salt : new Uint8Array(salt);
    const keyBytes = forge.pkcs5.pbkdf2(
      forge.util.encodeUtf8(masterPassword),
      this.bytesToBinary(saltBytes),
      100000,
      32,
      'sha256'
    );
    const key = this.binaryToBytes(keyBytes);
    return { key, salt: saltBytes };
  }

  /**
   * Exports a key to a string for sessionStorage caching.
   * Native mode: JWK string. Fallback mode: Base64 raw key bytes.
   * @param {CryptoKey|Uint8Array} key - The encryption key
   * @returns {Promise<string>} - Export string
   */
  async exportKey(key) {
    if (this.subtle && key && this.isNativeKey(key)) {
      const jwk = await this.subtle.exportKey('jwk', key);
      return JSON.stringify(jwk);
    }
    const keyBytes = key instanceof Uint8Array ? key : new Uint8Array(key);
    return this.arrayBufferToBase64(keyBytes.buffer);
  }

  /**
   * Imports a key string back into the format used by this context.
   * Accepts both JWK (native) and Base64 raw bytes (fallback).
   * @param {string} keyString - Export string
   * @returns {Promise<CryptoKey|Uint8Array>} - The imported key
   */
  async importKey(keyString) {
    const isJwk = typeof keyString === 'string' && keyString.trim().startsWith('{');
    if (this.subtle) {
      if (isJwk) {
        const jwk = JSON.parse(keyString);
        return await this.subtle.importKey(
          'jwk',
          jwk,
          { name: 'AES-GCM' },
          true,
          ['encrypt', 'decrypt']
        );
      }
      // Base64 raw bytes (exported in a fallback context)
      const rawBytes = this.base64ToArrayBuffer(keyString);
      return await this.subtle.importKey(
        'raw',
        rawBytes,
        { name: 'AES-GCM' },
        true,
        ['encrypt', 'decrypt']
      );
    }
    if (isJwk) {
      throw new Error('Stored key was created in a secure context and cannot be restored over plain HTTP.');
    }
    return this.base64ToBytes(keyString);
  }

  /**
   * Encrypts data using the derived key
   * @param {CryptoKey|Uint8Array} key - The encryption key
   * @param {Object} data - The data to encrypt
   * @returns {Promise<Object>} - The encrypted data and IV
   */
  async encrypt(key, data) {
    // Generate a random IV for this encryption
    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    // Convert data to JSON string and then to buffer
    const dataString = JSON.stringify(data);
    const dataBuffer = this.encoder.encode(dataString);

    if (this.subtle && key && this.isNativeKey(key)) {
      // Encrypt the data
      const encryptedBuffer = await this.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv: iv
        },
        key,
        dataBuffer
      );

      // Convert to Base64 for storage
      const encryptedBase64 = this.arrayBufferToBase64(encryptedBuffer);
      const ivBase64 = this.arrayBufferToBase64(iv);

      return {
        encryptedData: encryptedBase64,
        iv: ivBase64
      };
    }

    // Fallback: pure-JS AES-256-GCM
    const keyBytes = key instanceof Uint8Array ? key : new Uint8Array(key);
    const cipher = forge.cipher.createCipher('AES-GCM', this.bytesToBinary(keyBytes));
    cipher.start({ iv: forge.util.createBuffer(iv) });
    cipher.update(forge.util.createBuffer(dataBuffer));
    cipher.finish();
    // Web Crypto appends the 128-bit auth tag to the ciphertext
    const combinedBinary = cipher.output.getBytes() + cipher.mode.tag.getBytes();

    return {
      encryptedData: btoa(combinedBinary),
      iv: this.arrayBufferToBase64(iv)
    };
  }

  /**
   * Decrypts data using the derived key
   * @param {CryptoKey|Uint8Array} key - The decryption key
   * @param {string} encryptedBase64 - The encrypted data in Base64
   * @param {string} ivBase64 - The IV in Base64
   * @returns {Promise<Object>} - The decrypted data
   */
  async decrypt(key, encryptedBase64, ivBase64) {
    const encryptedBuffer = this.base64ToArrayBuffer(encryptedBase64);
    const iv = this.base64ToArrayBuffer(ivBase64);

    if (this.subtle && key && this.isNativeKey(key)) {
      // Decrypt the data
      const decryptedBuffer = await this.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: iv
        },
        key,
        encryptedBuffer
      );

      // Convert buffer back to string and parse JSON
      const decryptedString = this.decoder.decode(decryptedBuffer);
      return JSON.parse(decryptedString);
    }

    // Fallback: pure-JS AES-256-GCM
    const keyBytes = key instanceof Uint8Array ? key : new Uint8Array(key);
    const combinedBytes = new Uint8Array(encryptedBuffer);
    if (combinedBytes.length < 16) {
      throw new Error('Decryption failed: ciphertext too short.');
    }
    const ciphertextBytes = combinedBytes.subarray(0, combinedBytes.length - 16);
    const tagBytes = combinedBytes.subarray(combinedBytes.length - 16);

    const decipher = forge.cipher.createDecipher('AES-GCM', this.bytesToBinary(keyBytes));
    decipher.start({
      iv: forge.util.createBuffer(iv),
      tag: this.bytesToBinary(tagBytes)
    });
    decipher.update(forge.util.createBuffer(ciphertextBytes));
    if (!decipher.finish()) {
      throw new Error('Decryption failed: authentication tag mismatch.');
    }

    const plaintextBinary = decipher.output.getBytes();
    const plaintextBytes = this.binaryToBytes(plaintextBinary);
    const decryptedString = this.decoder.decode(plaintextBytes);
    return JSON.parse(decryptedString);
  }

  /**
   * Generates a secure random password
   * @param {Object} options - Password generation options
   * @returns {string} - The generated password
   */
  generatePassword(options = {}) {
    const defaults = {
      length: 16,
      uppercase: true,
      lowercase: true,
      numbers: true,
      symbols: true
    };

    const config = { ...defaults, ...options };

    let charset = '';
    if (config.uppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (config.lowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
    if (config.numbers) charset += '0123456789';
    if (config.symbols) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';

    // Ensure charset is not empty
    if (!charset) charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

    // Generate random bytes
    const randomValues = new Uint8Array(config.length);
    window.crypto.getRandomValues(randomValues);

    // Map random bytes to characters in the charset
    let password = '';
    for (let i = 0; i < config.length; i++) {
      password += charset[randomValues[i] % charset.length];
    }

    return password;
  }

  /**
   * Generates an authentication hash from the master password
   * This is different from the encryption key and is used for server authentication
   * @param {string} masterPassword - The user's master password
   * @param {string} email - The user's email
   * @param {Uint8Array|string} salt - Salt for hash derivation (can be Uint8Array or Base64 string)
   * @returns {Promise<Object>} - The auth hash and salt
   */
  async generateAuthHash(masterPassword, email, salt) {
    // If salt is a string, convert it back to Uint8Array
    if (typeof salt === 'string') {
      salt = this.base64ToArrayBuffer(salt);
      salt = new Uint8Array(salt);
    }

    // If no salt is provided, generate a new one
    if (!salt) {
      salt = window.crypto.getRandomValues(new Uint8Array(16));
    }
    const saltBytes = salt instanceof Uint8Array ? salt : new Uint8Array(salt);

    // Combine email and password for the auth material
    const authMaterial = email.toLowerCase() + ':' + masterPassword;

    if (this.subtle) {
      const authBuffer = this.encoder.encode(authMaterial);

      // Import as raw key material
      const keyMaterial = await this.subtle.importKey(
        'raw',
        authBuffer,
        { name: 'PBKDF2' },
        false,
        ['deriveBits']
      );

      // Derive bits for the auth hash
      const bits = await this.subtle.deriveBits(
        {
          name: 'PBKDF2',
          salt: saltBytes,
          iterations: 100000,
          hash: 'SHA-256'
        },
        keyMaterial,
        256 // 256 bits
      );

      // Convert to Base64 for storage and transmission
      const authHash = this.arrayBufferToBase64(bits);
      const saltBase64 = this.arrayBufferToBase64(saltBytes);

      return {
        authHash,
        salt: saltBase64
      };
    }

    // Fallback: pure-JS PBKDF2-SHA256
    const authBinary = forge.pkcs5.pbkdf2(
      forge.util.encodeUtf8(authMaterial),
      this.bytesToBinary(saltBytes),
      100000,
      32,
      'sha256'
    );
    const authHash = btoa(authBinary);
    const saltBase64 = this.arrayBufferToBase64(saltBytes.buffer);

    return {
      authHash,
      salt: saltBase64
    };
  }

  /**
   * Converts an ArrayBuffer to a Base64 string
   * @param {ArrayBuffer|Uint8Array} buffer - The buffer to convert
   * @returns {string} - Base64 string
   */
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Converts a Base64 string to an ArrayBuffer
   * @param {string} base64 - The Base64 string
   * @returns {ArrayBuffer} - The converted ArrayBuffer
   */
  base64ToArrayBuffer(base64) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Converts a Base64 string to a Uint8Array
   * @param {string} base64 - The Base64 string
   * @returns {Uint8Array} - The converted bytes
   */
  base64ToBytes(base64) {
    return new Uint8Array(this.base64ToArrayBuffer(base64));
  }

  /**
   * Checks whether a key is a native Web Crypto CryptoKey.
   * CryptoKey may be undefined in insecure (plain HTTP) contexts,
   * so it must be checked defensively.
   * @param {*} key - The key to check
   * @returns {boolean} - True if the key is a native CryptoKey
   */
  isNativeKey(key) {
    return typeof CryptoKey !== 'undefined' && key instanceof CryptoKey;
  }

  /**
   * Converts a Uint8Array to a binary (latin-1) string, as expected by node-forge
   * @param {Uint8Array} bytes - The bytes to convert
   * @returns {string} - Binary string
   */
  bytesToBinary(bytes) {
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, chunk);
    }
    return binary;
  }

  /**
   * Converts a binary (latin-1) string back to a Uint8Array
   * @param {string} binary - Binary string
   * @returns {Uint8Array} - The converted bytes
   */
  binaryToBytes(binary) {
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Compresses data using gzip-like algorithm
   * Note: This is a placeholder. In a real implementation, you would use
   * a proper compression library like pako or lz-string
   * @param {Object} data - The data to compress
   * @returns {string} - Compressed data as string
   */
  compressData(data) {
    // In a real implementation, this would use a compression library
    // For now, we'll just return the JSON string
    return JSON.stringify(data);
  }

  /**
   * Decompresses data
   * @param {string} compressedData - The compressed data
   * @returns {Object} - Decompressed data object
   */
  decompressData(compressedData) {
    // In a real implementation, this would use a decompression library
    // For now, we'll just parse the JSON string
    return JSON.parse(compressedData);
  }
}

// Create and export a singleton instance
const flashSyncCrypto = new FlashSyncCrypto();
export default flashSyncCrypto;
